import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos información personal que nos proporcionas directamente al realizar una compra, como tu nombre, dirección de correo electrónico y datos de pago. No compartimos esta información con terceros salvo lo necesario para procesar tu pedido.'
  }, {
    heading: 'Uso de la Información',
    text: 'Utilizamos tu información personal para: procesar y entregar tu compra, enviarte actualizaciones relacionadas con tu producto, mejorar nuestros servicios y comunicarnos contigo sobre ofertas relevantes (solo si has dado tu consentimiento).'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio web puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar algunas funcionalidades.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, contáctanos a través de los canales disponibles en nuestra página.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Aceptación de los Términos',
    text: 'Al acceder y utilizar este sitio web y adquirir nuestros productos digitales, aceptas estar sujeto a estos términos y condiciones. Si no estás de acuerdo con alguno de estos términos, te pedimos que no utilices nuestros servicios.'
  }, {
    heading: 'Productos Digitales',
    text: 'Los productos ofrecidos son guías digitales en formato PDF y materiales complementarios. El acceso se otorga de forma inmediata tras la confirmación del pago. Los resultados pueden variar según el esfuerzo y dedicación de cada persona.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Debido a la naturaleza digital de nuestros productos, todas las ventas son finales una vez que se ha entregado el acceso al material. Te recomendamos revisar toda la información disponible antes de realizar tu compra.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido incluido en nuestros productos digitales está protegido por derechos de autor. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'Nuestros productos son de carácter educativo e informativo. No garantizamos resultados específicos de ingresos. El éxito depende de múltiples factores individuales, incluyendo el esfuerzo, habilidades y condiciones del mercado.'
  }, {
    heading: 'Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones serán efectivas desde su publicación en esta página. El uso continuado del sitio constituye la aceptación de los términos modificados.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-[#1e2d4d] text-gray-400 py-10 px-4 border-t border-[#3a5280]" data-id="element-5">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4" data-id="element-6">
          <div className="flex items-center gap-6" data-id="element-7">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-gray-400 hover:text-[#c9a84c] transition-colors underline underline-offset-2" data-id="element-8">
              Política de Privacidad
            </button>
            <span className="text-gray-600" data-id="element-9">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-gray-400 hover:text-[#c9a84c] transition-colors underline underline-offset-2" data-id="element-10">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-sm text-gray-500" data-id="element-11">
            © {new Date().getFullYear()} Freelance desde Cero. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-12">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-13">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-14">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-[#1e2d4d]" data-id="element-15">
                <h3 className="text-xl font-bold text-white" data-id="element-16">
                  {content.title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white" aria-label="Cerrar" data-id="element-17">
                  <X className="w-5 h-5" data-id="element-18" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-19">
                <div className="space-y-6" data-id="element-20">
                  {content.sections.map((section, i) => <div key={i} data-id="element-21">
                      <h4 className="text-lg font-bold text-[#1e2d4d] mb-2" data-id="element-22">
                        {section.heading}
                      </h4>
                      <p className="text-gray-600 leading-relaxed" data-id="element-23">
                        {section.text}
                      </p>
                    </div>)}
                </div>

                <p className="mt-8 text-sm text-gray-400 text-center" data-id="element-24">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long'
              })}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}